import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from './ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import TextInput from 'components/forms/basefields/TextInput';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/contact-merge.scss';

// One warning of the convert preview
interface ConvertWarning {
    code: string;
    count: number;
}

// The convert preview response of the backend
interface ConvertPreview {
    id: number;
    name: string;
    entity_type: string;
    new_entity_type: string;
    warnings: ConvertWarning[];
    name_fields: { title: string, first_name: string, last_name: string } | null;
}

interface ContactConvertModalProps {
    contactId: number;
    targetEntityType: 'organization' | 'person';
    onConverted: (contact: { id: number, entity_type: string }) => void;
}

/*
 * ContactConvertModal.tsx
 * Converts an organization into a person or a person into an
 * organization. It shows what the conversion drops, lets the user
 * check the proposed person name fields and executes the conversion.
 */
const ContactConvertModal: React.FC<ContactConvertModalProps> = ({ contactId, targetEntityType, onConverted }) => {
    const { t } = useTranslation();
    const { revealModal, closeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const [nameFields, setNameFields] = useState({ title: '', first_name: '', last_name: '' });
    const [errors, setErrors] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    // Fetch the preview of the conversion
    const previewParams = useMemo(() => ({ entity_type: targetEntityType }), [targetEntityType]);
    const { response, loading } = useFetchData({ apiUrl: `get_contact_convert_preview/${contactId}`, params: previewParams });
    const preview = response as ConvertPreview | null;

    // Reveal the modal on first render
    useEffect(() => { revealModal() }, []);

    // Prefill the person name fields from the preview
    useEffect(() => {
        if (preview?.name_fields) {
            setNameFields({
                title: preview.name_fields.title || '',
                first_name: preview.name_fields.first_name || '',
                last_name: preview.name_fields.last_name || '',
            });
        }
    }, [preview]);

    // Execute the conversion and hand the converted contact to the parent
    const handleConvert = async () => {
        setSaving(true);
        try {
            const data: Record<string, string> = { entity_type: targetEntityType };
            if (targetEntityType === 'person') {
                data.title = nameFields.title;
                data.first_name = nameFields.first_name;
                data.last_name = nameFields.last_name;
            }
            const result = await saveData({ apiUrl: `post_contact_convert/${contactId}`, method: 'post', data });
            setFloatingAlert({ type: 'success', message: 'contact.convert.success_message' });
            closeModal();
            onConverted({ id: result?.data?.id ?? contactId, entity_type: result?.data?.entity_type ?? targetEntityType });
        } catch (error: any) {
            const responseData = error?.response?.data;
            setErrors(responseData && typeof responseData === 'object' ? Object.values(responseData).map(value => String(value)) : ['validation.contact.convert.invalid_request']);
            setSaving(false);
        }
    };

    // A person needs at least one name field
    const nameMissing = targetEntityType === 'person' && !nameFields.title.trim() && !nameFields.first_name.trim() && !nameFields.last_name.trim();

    return (
        <div className='contact-convert-modal'>
            <p className='contact-merge-search-explanation'>
                {t(targetEntityType === 'person' ? 'contact.convert.to_person_explanation' : 'contact.convert.to_organization_explanation')}
            </p>

            {loading === 'loading' ? (
                <div className='loader'></div>
            ) : (
                <>
                    {(errors.length > 0 || (preview && preview.warnings.length > 0)) && (
                        // Show the blocking errors and the warnings about dropped data
                        <div className={`contact-merge-warnings ${errors.length > 0 ? 'is-error' : ''}`}>
                            {errors.map((error, index) => (
                                <div key={`error-${index}`} className='contact-merge-warning'>
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    <span>{t(error, { defaultValue: error })}</span>
                                </div>
                            ))}
                            {preview?.warnings.map(warning => (
                                <div key={warning.code} className='contact-merge-warning'>
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                    <span>{t(`contact.convert.warnings.${warning.code}`, { count: warning.count })}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {targetEntityType === 'person' && preview && (
                        // Let the user check the proposed person name fields
                        <div className='formset add-edit-form'>
                            <div className='field-row'>
                                <div className='formfield' style={{ width: '35%' }}>
                                    <label htmlFor='convert_title'>{t('person.general.salutation_label')}</label>
                                    <TextInput name='convert_title'
                                               placeholder='person.general.salutation_placeholder'
                                               value={nameFields.title}
                                               onChange={(value) => setNameFields({ ...nameFields, title: value })} />
                                </div>
                                <div className='formfield' style={{ width: '65%' }}>
                                    <label htmlFor='convert_first_name'>{t('person.general.first_name_label')}</label>
                                    <TextInput name='convert_first_name'
                                               placeholder='person.general.first_name_placeholder'
                                               value={nameFields.first_name}
                                               onChange={(value) => setNameFields({ ...nameFields, first_name: value })} />
                                </div>
                            </div>
                            <div className='formfield'>
                                <label htmlFor='convert_last_name'>{t('person.general.last_name_label')}</label>
                                <TextInput name='convert_last_name'
                                           placeholder='person.general.last_name_placeholder'
                                           value={nameFields.last_name}
                                           onChange={(value) => setNameFields({ ...nameFields, last_name: value })} />
                            </div>
                        </div>
                    )}
                </>
            )}

            <div className='formset'>
                <div className='buttons-right'>
                    <SecondaryButton
                        onClick={() => closeModal()}
                        label='general.cancel'
                        size='small' />
                    <PrimaryButton
                        onClick={handleConvert}
                        label='contact.convert.confirm_button_label'
                        size='small'
                        loading={saving}
                        disabled={{ disabled: !preview || nameMissing }} />
                </div>
            </div>
        </div>
    );
};

export default ContactConvertModal;
